import { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import constants from "constants";
import { useLatestPolicy } from "repositories/office-policy-management-repository";
import { decodeId, encodeId, handleError } from "utils";

/**
 * Positive integer id from a route or query value.
 * Rejects blanks, decimals, and non-numeric text.
 * @param {unknown} value
 * @returns {number|null}
 */
export function parsePositiveIntegerId(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return null;
  const id = Number(text);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  return id;
}

/**
 * Numeric id from a route or query value.
 * Accepts a plain id (`27`) or an encoded id (`NDM=`).
 * @param {unknown} value
 * @returns {number|null}
 */
export function resolvePolicyId(value) {
  const direct = parsePositiveIntegerId(value);
  if (direct != null) return direct;
  if (value == null || String(value).trim() === "") return null;
  return parsePositiveIntegerId(decodeId(String(value).trim()));
}

/**
 * Optional `policyId` from the current URL search string.
 * @param {string} [search]
 * @returns {string|null} Raw query value when present, otherwise null.
 */
export function readOptionalPolicyId(search) {
  const params = new URLSearchParams(search || "");
  const raw = params.get("policyId") ?? params.get("PolicyId");
  if (raw == null || raw.trim() === "") return null;
  return raw.trim();
}

/**
 * Resolves a latest-policy link to the encoded assignment details path.
 * Folder and policy ids may be plain numbers or encoded values.
 * On failure, stays off the details route and exposes the same back behavior
 * as policy details.
 * @returns {{ hasError: boolean, goToPreviousScreen: () => void }}
 */
export function useLatestPolicyRedirect() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {
    policyFolderId: policyFolderIdParam,
    officePolicyFolderId,
  } = useParams();

  const policyFolderId = resolvePolicyId(
    policyFolderIdParam ?? officePolicyFolderId,
  );
  const policyIdParam = readOptionalPolicyId(location.search);
  const policyId =
    policyIdParam == null ? null : resolvePolicyId(policyIdParam);
  const policyIdInvalid = policyIdParam != null && policyId == null;
  const canRequest = policyFolderId != null && !policyIdInvalid;

  const { data, isError, error } = useLatestPolicy(
    policyFolderId,
    policyId,
    { enabled: canRequest },
  );

  const encodedAssignmentId = useMemo(() => {
    const assignmentId = parsePositiveIntegerId(data?.policyAssignmentId);
    if (assignmentId == null) return "";
    return encodeId(assignmentId);
  }, [data]);

  useEffect(() => {
    if (!encodedAssignmentId) return;
    history.replace(
      constants.routes.officePolicyManagements.officePolicyManagementDetails.replace(
        ":officePolicyId",
        encodedAssignmentId,
      ),
    );
  }, [encodedAssignmentId, history]);

  const hasError =
    !canRequest || isError || (data != null && !encodedAssignmentId);

  const toastedRef = useRef(false);
  useEffect(() => {
    if (!hasError || toastedRef.current) return;
    toastedRef.current = true;
    const message =
      error?.message || t("officePolicyManagement.failedToLoadLatestPolicy");
    handleError({ message });
  }, [hasError, error, t]);

  const goToPreviousScreen = useCallback(() => {
    if (window.history.length > 1) {
      history.goBack();
      return;
    }
    history.push(
      constants.routes.officePolicyManagements.officePolicyManagement,
    );
  }, [history]);

  return {
    hasError,
    goToPreviousScreen,
  };
}
